import { LlmAnnotation, LlmAnnotationsData, PlddtResidueData } from '../structure-data.models';
import { SmartSequenceAnnotation } from '../../shared/smart-seq-viewer/viewer/data-processing/seq-viewer-models';

export const TEXT_ANNOTATION_HIGHLIGHT_COLOR = '#42AD4B';
const DEFAULT_RESIDUE_BACKGROUND_COLOR = '#e8e8e8';

// Some llm_annotations sentences arrive double-UTF-8-encoded from the API itself — verified
// against the raw API response (curl, bypassing our own fetch/JSON parsing entirely) for
// P20248: symbols like '±', non-breaking spaces, and en-dashes get UTF-8-encoded once, and
// those resulting BYTES then get misread as Latin-1 and UTF-8-encoded again somewhere in the
// backend's extraction pipeline (looks like a PDF-table-extraction step — only tab-separated
// table content with symbols is affected; plain prose sentences are unaffected). This is a real
// source-data bug, reported upstream — this is a client-side stopgap for readability in the
// meantime, not a substitute for the backend fix (CSV exports/other API consumers still see the
// corrupted text).
//
// Deterministically reversible: every character in a corrupted run fits in a single byte
// (0-255), so re-reading those codepoints as raw bytes and decoding them as UTF-8 once recovers
// the original text. Falls back to the input untouched whenever that round-trip hits a codepoint
// outside the single-byte range or fails to decode as valid UTF-8 — so a sentence that was never
// corrupted (the common case) is guaranteed to pass through byte-for-byte unchanged, and a
// genuine (uncorrupted) 'Â'/'â' character elsewhere just fails the decode and is left alone.
const MOJIBAKE_MARKER = /[Ââ]/; // 'Â' / 'â' — the tell-tale lead byte of this corruption

export function fixMojibake(text: string): string {
  if (!text || !MOJIBAKE_MARKER.test(text)) return text;
  const codePoints = Array.from(text, (ch) => ch.codePointAt(0)!);
  if (codePoints.some((cp) => cp > 0xff)) return text;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(codePoints));
  } catch {
    return text;
  }
}

// Identifies "the same annotation" across depositions/re-fetches — shared by dedupeAnnotations
// (collapsing repeats) and the table's per-row "Show more"/"Show less" expand state (there's no
// stable id field on LlmAnnotation itself to key a Set/Map by).
export function getAnnotationKey(annotation: LlmAnnotation): string {
  return `${annotation.uniprotResidue}|${annotation.pubmedId}|${annotation.exact}|${annotation.sentence}`;
}

// Empirically measured against the real rendered Annotations column (via a throwaway Playwright
// script driving the actual dev-server app, including the bold exact-match highlight markup —
// not guessed, and not just raw text in isolation) rather than guessed: at the narrowest common
// desktop width this tab is expected to render at (1280px — there's no mobile-specific layout
// for this tab yet), the column is ~543px wide at this table's 16px/27.2px IBM Plex Sans
// styling, and 35 words (plus the ' ...' suffix truncateSentence appends) is the most that still
// wraps to exactly 5 lines there (36 wraps to 6). Wider screens render 35 words in fewer than 5
// lines (e.g. 4 at 1440px+), so this deliberately never lets the collapsed state exceed 5 lines
// anywhere, at the cost of sometimes triggering "Show more" a bit earlier than strictly
// necessary on a wide monitor.
export const ANNOTATION_SENTENCE_WORD_LIMIT = 35;

// Split on any run of whitespace, not just literal ' ' — some real llm_annotations sentences are
// extracted-PDF-table content that uses tabs (and, after fixMojibake, non-breaking spaces) as
// cell separators. Splitting on a bare ' ' treated a whole tab-separated run with no literal
// space in it as a single "word", undercounting real length by ~2-3x and letting the truncated
// preview run to 10+ lines instead of 5 — this is what actually fixes that, not just a style
// choice. Also normalizes the kept portion's internal whitespace to single spaces.
const WORD_SPLIT_REGEX = /\s+/;

export function sentenceNeedsTruncation(sentence: string): boolean {
  return !!sentence && sentence.trim().split(WORD_SPLIT_REGEX).length > ANNOTATION_SENTENCE_WORD_LIMIT;
}

export function truncateSentence(sentence: string): string {
  if (!sentenceNeedsTruncation(sentence)) return sentence;
  // Leading space before the ellipsis, matching the existing word-limit convention elsewhere
  // (macromolecules-tab's getShortenedBiologicalFunctionText, summary-tab's getShortenedText).
  return sentence.trim().split(WORD_SPLIT_REGEX).slice(0, ANNOTATION_SENTENCE_WORD_LIMIT).join(' ') + ' ...';
}

// Same bucketing thresholds/colors as heatmap.component.ts's pLDDT track
// (d3.scaleThreshold().domain([50, 70, 90])), kept in sync for visual consistency.
const PLDDT_BUCKETS: { min: number; label: string; color: string }[] = [
  { min: 90, label: 'Very high', color: '#0053D6' },
  { min: 70, label: 'High', color: '#65CBF3' },
  { min: 50, label: 'Low', color: '#FFDB13' },
  { min: -Infinity, label: 'Very low', color: '#FF7D45' },
];

function getPlddtBucket(score: number): { label: string; color: string } {
  const bucket = PLDDT_BUCKETS.find((b) => score >= b.min);
  return bucket ?? PLDDT_BUCKETS[PLDDT_BUCKETS.length - 1];
}

/**
 * A complex entry's confidence file covers the WHOLE assembly in one shared payload —
 * residueNumber runs continuously across every chain (e.g. chain A's residues, then chain B's,
 * for a combined total), not restarting at 1 per chain. `chains` gives each chain's own LOCAL
 * numbering (sequenceStart/sequenceEnd, matching that chain's own sequence indices) but not its
 * position within the combined arrays — that has to be derived from the running total of the
 * chains listed before it in `chains`, in the order they appear there.
 *
 * Returns the subset belonging to `chainId`, re-indexed back to that chain's own local 1-based
 * numbering so it lines up with activeSequence(). Returns the input unchanged for a monomer
 * entry (no `chains` breakdown) or if the requested chain isn't listed.
 */
export function sliceChainConfidenceData(plddtData: PlddtResidueData, chainId: string): PlddtResidueData {
  const chains = plddtData.chains;
  if (!chains || chains.length <= 1) return plddtData;

  const chainIndex = chains.findIndex((c) => c.label_asym_id === chainId);
  if (chainIndex === -1) return plddtData;

  const globalOffset = chains.slice(0, chainIndex).reduce((sum, c) => sum + (c.sequenceEnd - c.sequenceStart + 1), 0);
  const chainLength = chains[chainIndex].sequenceEnd - chains[chainIndex].sequenceStart + 1;
  const globalStart = globalOffset + 1;
  const globalEnd = globalOffset + chainLength;

  const residueNumber: number[] = [];
  const confidenceScore: number[] = [];
  for (let i = 0; i < plddtData.residueNumber.length; i++) {
    const num = plddtData.residueNumber[i];
    if (num >= globalStart && num <= globalEnd) {
      residueNumber.push(num - globalOffset);
      confidenceScore.push(plddtData.confidenceScore[i]);
    }
  }

  return { residueNumber, confidenceScore };
}

/**
 * Different PDB depositions of the same UniProt accession commonly repeat the exact same
 * annotation verbatim (verified against a real accession with 2 depositions sharing all 4
 * annotation sentences) — collapse those repeats to a single row. Keyed on residue + sentence +
 * exact match + source publication rather than a full deep-equal, since fields like pdbChain/
 * pdbResidue are deposition-specific and would otherwise defeat the dedup for what is really the
 * same annotation.
 */
export function dedupeAnnotations(annotations: LlmAnnotation[]): LlmAnnotation[] {
  const seen = new Set<string>();
  return annotations.filter((annotation) => {
    const key = getAnnotationKey(annotation);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function groupBy<T>(items: T[], key: (item: T) => string): { [key: string]: T[] } {
  return items.reduce((groups: { [key: string]: T[] }, item) => {
    const groupKey = key(item);
    (groups[groupKey] ??= []).push(item);
    return groups;
  }, {});
}

export function getCircleAnnotationsForSeqViewer(annotations: LlmAnnotation[], rangeStart: number = 1): SmartSequenceAnnotation {
  // Use uniprotResidue, not pdbResidue: our sequence viewer is numbered against the chain's
  // own local sequence, while pdbResidue is the PDB deposition's own numbering, which can
  // carry a construct-specific offset that doesn't correspond to positions in that sequence.
  // uniprotResidue itself is absolute UniProt numbering — for a chain that isn't the start of
  // its UniProt accession (e.g. one fragment of a cleaved polyprotein), rangeStart converts it
  // to the chain's own local 1-based numbering; a no-op (rangeStart 1) for an ordinary chain.
  const dataForSeqViewer = annotations
    .slice()
    .sort((a, b) => a.uniprotResidue - b.uniprotResidue)
    .filter((annotation, index, self) => index === self.findIndex((a) => a.uniprotResidue === annotation.uniprotResidue))
    .map((annotation) => {
      // Not re-deduped by sentence: the residue-details panel's "N text-mined annotations"
      // count must equal the table's row count for this residue (input is already deduped).
      const annotationsForResidue = annotations.filter((each) => each.uniprotResidue === annotation.uniprotResidue);
      return {
        residueIndex: annotation.uniprotResidue - rangeStart + 1,
        value: 'has annotation',
        extraData: annotationsForResidue,
      };
    });

  return {
    name: 'Text Annotation (AI)',
    identifier: 'text-annotation-ai',
    scaleType: 'ordinal',
    scaleDomain: ['has annotation'],
    scaleRange: [TEXT_ANNOTATION_HIGHLIGHT_COLOR],
    rendering: 'Background',
    data: dataForSeqViewer,
  };
}

/**
 * Fallback used while real per-residue pLDDT data hasn't loaded yet (or isn't available):
 * every residue gets the same neutral bottom-border colour, purely so the grid reads as a
 * sequence panel rather than plain unboxed text. AFDB predictions have no experimental
 * validation/outlier data, so unlike pdbeconnect's per-annotation outlier categories, this
 * is intentionally flat and doesn't claim any real per-residue meaning.
 */
export function getUniformBackgroundAnnotation_ex(sequence: string): SmartSequenceAnnotation {
  return {
    name: 'Residue',
    identifier: 'afdb-residue-background',
    scaleType: 'ordinal',
    scaleDomain: ['residue'],
    scaleRange: [DEFAULT_RESIDUE_BACKGROUND_COLOR],
    rendering: 'Underline',
    data: Array.from({ length: sequence.length }, (_, i) => ({ residueIndex: i + 1, value: 'residue' })),
  };
}

/**
 * Colours each residue's bottom border by its real per-residue pLDDT confidence bucket,
 * matching the same thresholds/colours already used for the pLDDT heatmap track
 * (heatmap.component.ts) and the Model Confidence legend (legends-panel.component.ts).
 * Falls back to the flat neutral border while plddtData hasn't loaded yet (or for an entry
 * that has none).
 */
export function getUniformBackgroundAnnotation(sequence: string, plddtData: PlddtResidueData | undefined): SmartSequenceAnnotation {
  if (!plddtData?.residueNumber?.length || !plddtData?.confidenceScore?.length) {
    return getUniformBackgroundAnnotation_ex(sequence);
  }

  return {
    name: 'Model confidence (pLDDT)',
    identifier: 'afdb-plddt-background',
    scaleType: 'ordinal',
    scaleDomain: ['Very high', 'High', 'Low', 'Very low'],
    scaleRange: ['#0053D6', '#65CBF3', '#FFDB13', '#FF7D45'],
    rendering: 'Underline',
    data: plddtData.residueNumber.map((residueIndex, i) => ({
      residueIndex,
      value: getPlddtBucket(plddtData.confidenceScore[i]).label,
      extraData: { score: plddtData.confidenceScore[i] },
    })),
  };
}

export interface PublicationAuthorYear {
  // e.g. "Stepanyuk GA et al." — the real API's `authors` array is expected to hold each author
  // already formatted as family name + initials, first author shown with "et al." only when
  // there's more than one.
  author?: string;
  year?: string;
}

/**
 * Formats the Publication column's author/year display, e.g. author "Stepanyuk GA et al." and
 * year "2016" as two SEPARATE fields (not one combined string) so the caller can always render
 * them on their own line each — relying on a combined string wrapping naturally at the column's
 * fixed width made the line break land in different places depending on each row's author-name
 * length (sometimes one line, sometimes two), which read as inconsistent/broken. Returns
 * undefined when there's nothing to show at all (empty authors AND no year), so the caller can
 * hide the block entirely rather than render nothing.
 */
export function getPublicationAuthorYear(authors: string[] | undefined, publicationYear: string | number | null | undefined): PublicationAuthorYear | undefined {
  const firstAuthor = authors?.[0];
  const hasYear = publicationYear !== null && publicationYear !== undefined && publicationYear !== '';
  if (!firstAuthor && !hasYear) return undefined;

  const author = firstAuthor ? `${firstAuthor}${authors!.length > 1 ? ' et al.' : ''}` : undefined;
  const year = hasYear ? `${publicationYear}` : undefined;
  return { author, year };
}

/**
 * Publication licences don't always permit sharing the mined sentence text — anything other
 * than exactly 'Y' (null, '', 'N', or any other value) means the text is copyright-restricted
 * and must not be shown.
 */
export function isLicenseRestricted(permittingLicense: string | null | undefined): boolean {
  return permittingLicense !== 'Y';
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function getHighlightedSentence(sentence: string, exact: string): string {
  if (!exact) return sentence;
  const regex = new RegExp(escapeRegExp(exact), 'g');
  return sentence.replace(regex, `<strong class="clickable-strong">${exact}</strong>`);
}

function strictEncode(value: string): string {
  return encodeURIComponent(value)
    .replace(/-/g, '%2D')
    .replace(/_/g, '%5F')
    .replace(/\./g, '%2E');
}

export function getSentenceArticleLink(sentence: string, exact: string, pmcId: string): string {
  const escapedWord = escapeRegExp(exact);
  const regex = new RegExp(escapedWord, 'g');
  const match = regex.exec(sentence);
  if (!match) return `https://pmc.ncbi.nlm.nih.gov/articles/${pmcId}/`;

  const startIndex = match.index;
  const endIndex = startIndex + exact.length;

  // Scan outward from the matched term until the nearest bracket (or the sentence edge) on
  // each side, so the PMC deep-link context stays a short, specific snippet instead of the
  // whole sentence. Stops on the FIRST bracket found, including one immediately adjacent to
  // the term itself (e.g. "(Arg123)") — an earlier version required a bracket to sit 2+
  // characters away, which meant that common case fell through to the sentence's edges.
  let left = startIndex - 1;
  while (left >= 0) {
    const char = sentence[left];
    if (char === '(' || char === ')') break;
    left--;
  }

  let right = endIndex;
  while (right < sentence.length) {
    const char = sentence[right];
    if (char === '(' || char === ')') break;
    right++;
  }

  const context = sentence.slice(left + 1, right).trim();
  const encodedContext = strictEncode(context);
  return `https://pmc.ncbi.nlm.nih.gov/articles/${pmcId}/#:~:text=${encodedContext}`;
}

/**
 * Flattens one llm_annotations API response into the tab's LlmAnnotationsData. Shared by the
 * store effect (active-chain load) and the sidebar's "Download annotations" (which fetches every
 * chain's accession on demand), so both see identical, cleaned, deduped rows.
 */
export function parseLlmAnnotationsResponse(data: any, uniprotAccession: string): LlmAnnotationsData {
  const rawEntry = data?.[uniprotAccession];
  const annotations: LlmAnnotation[] = [];
  // `data` is now an array of every PDB deposition for this UniProt accession, each
  // with its own providerList/residueList nesting — a deposition-per-protein
  // structure, not one PDB entry. Different depositions of the same protein commonly
  // repeat identical annotation sentences verbatim, so the flattened list is deduped
  // below.
  for (const deposition of rawEntry?.data ?? []) {
    for (const provider of deposition?.providerList ?? []) {
      for (const residue of provider?.residueList ?? []) {
        for (const annotation of residue?.additionalData ?? []) {
          annotations.push({ ...annotation, pdbId: deposition?.pdbId });
        }
      }
    }
  }
  // The API itself sometimes returns double-UTF-8-encoded sentence/exact text (a
  // real backend data bug, reported — see fixMojibake's own comment). Fixed here,
  // once, right after parsing, so every downstream consumer (table, search, dedupe
  // key, the bold-highlight regex matching `exact` against `sentence`) sees the
  // corrected text consistently.
  const cleanedAnnotations = annotations.map((a) => ({
    ...a,
    sentence: fixMojibake(a.sentence),
    exact: fixMojibake(a.exact),
  }));
  return {
    dataType: rawEntry?.dataType,
    data: dedupeAnnotations(cleanedAnnotations),
    uniprotAccession,
  };
}

// Columns and headers agreed with the bioinformatics team (2026-10-06). primaryCitation and
// permittingLicense are deliberately not exported.
export const ANNOTATION_CSV_COLUMNS: { key: keyof LlmAnnotation; header: string }[] = [
  { key: 'uniprotAccession', header: 'UniProt Acc' },
  { key: 'uniprotResidue', header: 'Residue (UniProt)' },
  { key: 'pdbId', header: 'PDB ID' },
  { key: 'pdbChain', header: 'PDB Chain' },
  { key: 'pdbResidue', header: 'Residue (PDB)' },
  { key: 'sentence', header: 'Annotation' },
  { key: 'exact', header: 'Exact' },
  { key: 'aiScore', header: 'AI Score' },
  { key: 'authors', header: 'Authors' },
  { key: 'publicationYear', header: 'Year' },
  { key: 'pubmedId', header: 'PubMed Id' },
  { key: 'pmcId', header: 'PMC ID' },
  { key: 'doi', header: 'DOI' },
];

export function toCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = Array.isArray(value) ? value.join('; ') : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Copyright-restricted rows (see isLicenseRestricted) never export their mined text — the
 * sentence is replaced with the same message the table shows and `exact` is left blank.
 */
export function annotationsToCsv(annotations: LlmAnnotation[], restrictedMessage: string): string {
  const header = ANNOTATION_CSV_COLUMNS.map((column) => toCsvCell(column.header)).join(',');
  const rows = annotations.map((annotation) => {
    const restricted = isLicenseRestricted(annotation.permittingLicense);
    return ANNOTATION_CSV_COLUMNS.map(({ key }) => {
      if (restricted && key === 'sentence') return toCsvCell(restrictedMessage);
      if (restricted && key === 'exact') return '';
      return toCsvCell(annotation[key]);
    }).join(',');
  });
  return [header, ...rows].join('\r\n');
}

// e.g. "annotation-2026-10-06T09_27_50.892Z.csv" — ':' isn't allowed in Windows file names.
export function getAnnotationCsvFileName(date: Date = new Date()): string {
  return `annotation-${date.toISOString().replace(/:/g, '_')}.csv`;
}

export function triggerCsvDownload(csv: string, fileName: string): void {
  // BOM so Excel opens the UTF-8 text (author names, Greek letters in sentences) correctly.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
